import { components, paths } from 'src/generated/v1';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { $api } from './api';

export type CaseItem = components['schemas']['Case'];
export type ConnectorUpdate = components['schemas']['ConnectorUpdate'];
export type Connector = components['schemas']['Connector'];
export type SpecificFieldUpdate = components['schemas']['SpecificFieldUpdate'];
export type MappingListItem = components['schemas']['MappingListItem'];

function invalidate(queryClient: ReturnType<typeof useQueryClient>, mapping_id?: number) {
  if (mapping_id) {
    queryClient.refetchQueries({
      queryKey: ['connectorMappings', mapping_id],
      exact: true,
    });
  } else {
    queryClient.refetchQueries({
      queryKey: ['connectorMappings'],
    });
  }
}

export const useConnectorMutations = () => {
  const queryClient = useQueryClient();

  const invalidate = () => {
    const toInvalidate = [
      ['get', '/shop_connectors/templates/{template_id}'],
      ['get', '/shop_connectors/templates/'],
      ['get', '/shop_connectors/'],
      ['get', '/shop_connectors/{connector_id}'],
      ['get', '/shop_connectors/{connector_id}/tasks/'],
      ['get', '/shop_connectors/{connector_id}/errors/'],
    ];

    toInvalidate.forEach(query => {
      queryClient.invalidateQueries({ queryKey: query });
    });
  };

  const testConnectionMutation = $api.useMutation('post', '/shop_connectors/{connector_id}/test_connection');

  const deactivateMutation = $api.useMutation('delete', '/shop_connectors/{connector_id}', {
    onSuccess() {
      invalidate();
    },
  });
  const updateMutation = $api.useMutation('put', '/shop_connectors/{connector_id}', {
    onSuccess() {
      invalidate();
    },
  });
  const createMutation = $api.useMutation('post', '/shop_connectors/', {
    onSuccess() {
      invalidate();
    },
  });
  const runOperationMutation = $api.useMutation(
    'post',
    '/shop_connectors/{connector_id}/operations/{operation_id}/run/',
    {
      onSuccess() {
        invalidate();
      },
    },
  );

  const updateSettingsMutation = $api.useMutation('put', '/shop_connectors/{connector_id}/settings', {
    onSuccess() {
      invalidate();
    },
  });

  return {
    testConnectionMutation,
    deactivateMutation,
    updateMutation,
    createMutation,
    runOperationMutation,
    updateSettingsMutation,
  };
};

export const useConnectorTemplateQuery = (id: number) => {
  return $api.useQuery('get', '/shop_connectors/templates/{template_id}', {
    params: {
      path: {
        template_id: id,
      },
    },
  });
};

export const useConnectorTemplatesQuery = () => {
  return $api.useQuery('get', '/shop_connectors/templates/');
};

export const useConnectorProductMappingsQuery = (
  params: paths['/shop_connectors/mappings/product']['get']['parameters']['query'],
) => {
  return $api.useQuery('get', '/shop_connectors/mappings/product', {
    params: {
      query: params,
    },
  });
};

export const useConnectorProductMappingQuery = (mapping_id: number) => {
  return $api.useQuery('get', `/shop_connectors/mappings/product/{mapping_id}/`, {
    params: {
      path: { mapping_id },
    },
    queryKey: ['connectorMappings', mapping_id],
  });
};

export const useConnectorLocationsQuery = (connectorId: number) => {
  return $api.useQuery('get', `/shop_connectors/{connector_id}/location`, {
    params: {
      path: { connector_id: connectorId },
    },
    queryKey: ['connectorMappings', connectorId],
  });
};

export const useConnectorLocationMappingsQuery = (connectorId: number) => {
  return $api.useQuery('get', `/shop_connectors/{connector_id}/mappings/location`, {
    params: {
      path: { connector_id: connectorId },
    },
    queryKey: ['connectorMappings', connectorId],
  });
};

export const useConnectorLocationMappingQuery = (mapping_id: number) => {
  return $api.useQuery('get', `/shop_connectors/mappings/location/{mapping_id}/`, {
    params: {
      path: { mapping_id },
    },
    queryKey: ['connectorMappings', mapping_id],
  });
};

export const useConnectorMappingMutations = () => {
  const queryClient = useQueryClient();

  const updateProductMappingMutation = $api.useMutation('put', '/shop_connectors/mappings/product/{mapping_id}/', {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['get', '/shop_connectors/mappings/product'],
      });
    },
  });

  const updateLocationMappingMutation = $api.useMutation(
    'put',
    '/shop_connectors/mappings/location/{mapping_id}/{warehouse_id}',
    {
      onSuccess() {
        invalidate(queryClient);
      },
    },
  );

  const deleteProductMapping = $api.useMutation('delete', '/shop_connectors/mappings/product/{mapping_id}/', {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['get', '/shop_connectors/mappings/product'],
      });
    },
  });

  return { updateProductMappingMutation, updateLocationMappingMutation, deleteProductMapping };
};

export const useConnectorsQuery = (
  params?: paths['/shop_connectors/']['get']['parameters']['query'] & { enabled?: boolean },
) => {
  return $api.useQuery('get', '/shop_connectors/', {
    params: {
      query: params,
    },
    enabled: params?.enabled,
  });
};

export const useConnectorQuery = (id: number) => {
  return $api.useQuery('get', '/shop_connectors/{connector_id}', {
    params: {
      path: {
        connector_id: id,
      },
    },
  });
};

export const useConnectorTasksQuery = (
  id: number,
  params: paths['/shop_connectors/{connector_id}/tasks/']['get']['parameters']['query'],
) => {
  return $api.useQuery('get', '/shop_connectors/{connector_id}/tasks/', {
    params: {
      path: {
        connector_id: id,
      },
      query: params,
    },
  });
};

export const useConnectorErrorsQuery = (
  id: number,
  params: paths['/shop_connectors/{connector_id}/errors/']['get']['parameters']['query'],
) => {
  return $api.useQuery(
    'get',
    '/shop_connectors/{connector_id}/errors/',
    {
      params: {
        path: {
          connector_id: id,
        },
        query: params,
      },
    },
    // keepPreviousData: without it, data flips to undefined on every page
    // change, maxPage collapses to 1 and the next button stays clickable
    // past the last page.
    { placeholderData: keepPreviousData },
  );
};
